import { useMemo } from "react";
import { useMcpEndpoints, type McpEndpoint } from "./use-mcp-endpoints";
import { useWrappedApis } from "./use-wrapped-apis";
import {
  getRecommendedAttachment,
  TOOL_COUNT_THRESHOLD,
  type AttachmentPlan,
} from "../lib/attachment-recommender";

/**
 * Sprint 16f (S16F.09) — workspace tool-count + scale detection. Composes the
 * `list_mcp_endpoints` and `list_wrapped_apis` queries (each already TanStack-
 * cached: 60s / 20s) and derives whether the umbrella tool list is large enough
 * that hosts should attach scoped endpoints instead. Drives the Connect dropdown
 * topology guidance and the Settings → Endpoints recommendation banner.
 */
export interface WorkspaceToolCount {
  /** Total tools on the umbrella `/mcp` endpoint (which exposes everything). */
  totalToolCount: number;
  /** path → tool_count for every endpoint in the catalogue. */
  byEndpoint: Record<string, number>;
  threshold: number;
  exceedsThreshold: boolean;
  /** Slugs of wrappers the current user has a credential set for. */
  credentialledWrappers: string[];
  /** True when this session has hit `/mcp` (umbrella) — drives the warning badge. */
  umbrellaAttached: boolean;
  /** Ordered attachment plan (scoped vs umbrella) — shared with the recommender. */
  plan: AttachmentPlan;
  endpoints: McpEndpoint[];
  isLoading: boolean;
}

export function useToolCountForWorkspace(): WorkspaceToolCount {
  const endpointsQuery = useMcpEndpoints();
  const wrappersQuery = useWrappedApis();

  return useMemo(() => {
    const endpoints = endpointsQuery.data?.endpoints ?? [];
    const umbrella = endpoints.find((e) => e.path === "/mcp") ?? null;
    // `/mcp` is the "everything" surface, so its tool_count is the workspace
    // total. Fall back to the largest endpoint count if the umbrella is filtered
    // out for this caller's role.
    const totalToolCount =
      umbrella?.tool_count ?? endpoints.reduce((max, e) => Math.max(max, e.tool_count), 0);

    const byEndpoint: Record<string, number> = {};
    for (const e of endpoints) byEndpoint[e.path] = e.tool_count;

    const credentialledWrappers = (wrappersQuery.data ?? [])
      .filter((w) => w.my_credential_set && !w.archived)
      .map((w) => w.slug);

    const plan = getRecommendedAttachment({ endpoints, totalToolCount, credentialledWrappers });

    return {
      totalToolCount,
      byEndpoint,
      threshold: TOOL_COUNT_THRESHOLD,
      exceedsThreshold: plan.exceedsThreshold,
      credentialledWrappers,
      umbrellaAttached: !!umbrella?.last_seen,
      plan,
      endpoints,
      isLoading: endpointsQuery.isLoading || wrappersQuery.isLoading,
    };
  }, [
    endpointsQuery.data,
    endpointsQuery.isLoading,
    wrappersQuery.data,
    wrappersQuery.isLoading,
  ]);
}
